const {trackGenerateLead} = require('../../analytics/generateLead');

const CONTACT_FORM_LEAD_VALUE = 48;
const CURRENCY_SELECT_ID = 'currency_choice_1';

function initializeContactForm(formElement) {
    const formType = formElement.dataset.formType; // Odczytujemy typ formularza (CS lub HR)
    if (!formType) return;

    const config = window.angularAppData['contactForm' + formType];
    if (!config) {
        console.error(`Error: Configuration for contactForm${formType} is missing.`);
        return;
    }

    const messageContainer = formElement.querySelector('.form-message');
    const submitButton = formElement.querySelector('button[type="submit"]');
    const originalButtonText = config.texts.submit;
    submitButton.textContent = originalButtonText;

    formElement.addEventListener('submit', async (event) => {
        event.preventDefault();

        messageContainer.textContent = '';
        messageContainer.className = 'form-message';

        const validationError = validateForm(formElement, config);
        if (validationError) {
            showMessage(validationError, 'error');
            return;
        }

        submitButton.disabled = true;
        submitButton.textContent = config.texts.sending;

        try {
            const recaptchaToken = await getRecaptchaToken();
            const fileInput = formElement.querySelector('input[type="file"]');
            const file = fileInput ? fileInput.files[0] : null;

            if (file) {
                const reader = new FileReader();
                reader.onload = (e) => sendFormData(formElement, config, e.target.result, recaptchaToken);
                reader.onerror = () => {
                    submitButton.disabled = false;
                    submitButton.textContent = originalButtonText;
                };
                reader.readAsDataURL(file);
            } else {
                sendFormData(formElement, config, null, recaptchaToken);
            }
        } catch (error) {
            showMessage(error.message, 'error');
            submitButton.disabled = false;
            submitButton.textContent = originalButtonText;
        }
    });

    async function getRecaptchaToken() {
        const recaptchaConfig = window.angularAppData?.recaptchaV3 || {};
        const siteKey = recaptchaConfig.siteKey;
        const action = recaptchaConfig.actions?.contact || 'contact_form';

        if (!siteKey) {
            throw new Error('reCAPTCHA is not configured.');
        }

        await loadRecaptchaScript(siteKey);
        await waitForRecaptchaReady();

        return await executeRecaptcha(siteKey, action);
    }

    // Sprawdzamy obecność api.js załadowanego z parametrem render dla naszego klucza,
    // a nie samo istnienie window.grecaptcha. Na stronach usług formularz opinii ładuje
    // api.js bez parametru render, więc grecaptcha istnieje, ale klucz v3 nie jest
    // zarejestrowany i execute() rzuca "Invalid site key or not loaded in api.js".
    function findRecaptchaScript(siteKey) {
        const renderParam = `render=${encodeURIComponent(siteKey)}`;
        const scripts = document.querySelectorAll('script[src*="/recaptcha/api.js"]');

        return Array.from(scripts).find(script => script.src.includes(renderParam)) || null;
    }

    function loadRecaptchaScript(siteKey) {
        const existingScript = findRecaptchaScript(siteKey);

        if (existingScript) {
            if (existingScript.dataset.recaptchaV3Loaded === 'true' || document.readyState === 'complete') {
                return Promise.resolve();
            }

            return new Promise((resolve, reject) => {
                existingScript.addEventListener('load', resolve, { once: true });
                existingScript.addEventListener('error', () => reject(new Error('reCAPTCHA verification failed.')), { once: true });
            });
        }

        return new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;
            script.async = true;
            script.defer = true;
            script.dataset.recaptchaV3 = 'contact-form';
            script.onload = () => {
                script.dataset.recaptchaV3Loaded = 'true';
                resolve();
            };
            script.onerror = () => reject(new Error('reCAPTCHA verification failed.'));
            document.head.appendChild(script);
        });
    }

    function waitForRecaptchaReady() {
        return new Promise((resolve, reject) => {
            if (typeof window.grecaptcha?.ready !== 'function') {
                reject(new Error('reCAPTCHA verification failed.'));
                return;
            }

            window.grecaptcha.ready(resolve);
        });
    }

    // Klient dla dołożonego klucza rejestruje się asynchronicznie, a grecaptcha.ready()
    // wywołuje callback natychmiast, gdy na stronie działa już inny widget reCAPTCHA.
    // Dlatego ponawiamy execute(), zamiast przerywać na pierwszym błędzie.
    async function executeRecaptcha(siteKey, action) {
        const retryDelay = 250;
        const attempts = 32;

        for (let attempt = 0; attempt < attempts; attempt++) {
            try {
                return await window.grecaptcha.execute(siteKey, { action });
            } catch (error) {
                await new Promise(resolve => setTimeout(resolve, retryDelay));
            }
        }

        throw new Error('reCAPTCHA verification failed.');
    }

    async function sendFormData(form, config, attachmentData, recaptchaToken) {
        const formData = new FormData(form);
        formData.append('action', 'send-contact-message');
        formData.append('_ajax_nonce', config.nonce);
        formData.append('recaptchaToken', recaptchaToken);
        if (attachmentData) {
            formData.delete(form.querySelector('input[type="file"]').name); // Usuń obiekt [File]
            formData.append('attachment', attachmentData); // Dodaj string base64
        }

        try {
            const response = await fetch(config.ajax_url, { method: 'POST', body: formData });
            const result = await response.json();

            if (result.success) {
                showMessage(result.data.message, 'success');
                form.reset();
                const currencyElement = document.getElementById(CURRENCY_SELECT_ID);
                if (currencyElement) {
                    trackGenerateLead(CONTACT_FORM_LEAD_VALUE, currencyElement.value, 'contact_form', formData.get('email'));
                }
            } else {
                throw new Error(result.data.message || 'An unknown error occurred.');
            }
        } catch (error) {
            showMessage(error.message, 'error');
        } finally {
            submitButton.disabled = false;
            submitButton.textContent = originalButtonText;
        }
    }

    function validateForm(form, config) {
        const errors = config.errors;
        let error = null;

        form.querySelectorAll('.is-invalid').forEach(el => el.classList.remove('is-invalid'));

        const authorInput = form.querySelector('[name="author"]');
        const emailInput = form.querySelector('[name="email"]');
        const contentInput = form.querySelector('[name="content"]');

        if (!authorInput?.value.trim()) error = errors.name_required;
        else if (!emailInput?.value.trim()) error = errors.email_required;
        else if (!/^\S+@\S+\.\S+$/.test(emailInput.value)) error = errors.email_invalid;
        else if (!contentInput?.value.trim()) error = errors.content_required;

        const fileInput = form.querySelector('input[type="file"]');
        const termsInput = form.querySelector('[name="terms"]');

        if (!error && fileInput?.files[0]) {
            const file = fileInput.files[0];
            if (file.type !== 'application/pdf') error = errors.file_type;
            else if (file.size > 10485760) error = errors.file_size;
        }
        if (!error && termsInput && !termsInput.checked) {
            error = errors.terms_required;
        }

        if (error) {
            if (error === errors.name_required) authorInput.classList.add('is-invalid');
        }

        return error;
    }

    function showMessage(message, type) {
        messageContainer.textContent = message;
        messageContainer.className = `form-message is-visible ${type === 'success' ? 'is-success' : 'is-error'}`;
    }
}

document.addEventListener('DOMContentLoaded', () => {
    const allContactForms = document.querySelectorAll('[data-form-type]');

    allContactForms.forEach(form => {
        initializeContactForm(form);
    });
});
